import * as Sentry from "@sentry/react";

interface CaptureErrorOptions {
  extra?: Record<string, any>;
  tags?: Record<string, string>;
  level?: "fatal" | "error" | "warning" | "log" | "info" | "debug";
  user?: {
    id?: string;
    email?: string;
    username?: string;
  };
}

/**
 * Centralized error tracking utility for Sentry
 * @param error - Error object or error message string
 * @param options - Additional context and metadata for the error
 * @returns The Sentry event ID
 */
export const captureError = (
  error: Error | string,
  options?: CaptureErrorOptions
): string => {
  const errorObject = typeof error === "string" ? new Error(error) : error;

  return Sentry.captureException(errorObject, {
    extra: options?.extra,
    tags: options?.tags,
    level: options?.level,
    user: options?.user,
  });
};

/**
 * Capture a message (non-error event) in Sentry
 * @param message - The message to log
 * @param options - Additional context and metadata
 * @returns The Sentry event ID
 */
export const captureMessage = (
  message: string,
  options?: CaptureErrorOptions
): string => {
  return Sentry.captureMessage(message, {
    extra: options?.extra,
    tags: options?.tags,
    level: options?.level || "info",
  });
};

/**
 * Identify the current user in Sentry for error tracking
 * @param id - User ID
 * @param name - User name
 * @param email - User email
 */
export const identifyUser = ({
  id,
  name,
  email,
}: {
  id: string;
  name: string;
  email: string;
}): void => {
  Sentry.setUser({
    id,
    email,
    username: name,
  });
};
